import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../Home/theme.css'; // Import the CSS file with color variables
import { FaPaperPlane, FaPause, FaPlay, FaStar } from 'react-icons/fa';

import vid9 from '../../../Assesst/vid3.mp4';
import thm9 from '../../../Assesst/2026-01-10.webp';

const VideoHero = () => {
  const [isMusicOn, setIsMusicOn] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [showLoaderImage, setShowLoaderImage] = useState(true);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  // Cloudinary loader image URL
  const cloudinaryLoaderImage = thm9;
  
  // Cloudinary video URL - Updated with your new link
  const cloudinaryVideoUrl = vid9;

  useEffect(() => {
    // Add Bell MT and Inter fonts
    const style = document.createElement('style');
    style.textContent = `
      @import url('https://fonts.cdnfonts.com/css/bell-mt');
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
      
      .bell-mt-primary {
        font-family: 'Bell MT', serif !important;
      }
      
      .inter-secondary {
        font-family: 'Inter', sans-serif !important;
      }

      /* Responsive typography - Mobile First Approach */
      @media (max-width: 360px) {
        .hero-title {
          font-size: 1.4rem !important;
          line-height: 1.2 !important;
        }
        .hero-description {
          font-size: 0.65rem !important;
          line-height: 1.3 !important;
        }
        .hero-stats {
          gap: 0.25rem !important;
        }
        .hero-stat-item {
          padding: 0.35rem !important;
        }
        .hero-stat-number {
          font-size: 0.9rem !important;
        }
        .hero-stat-text {
          font-size: 0.45rem !important;
        }
      }

      @media (min-width: 361px) and (max-width: 480px) {
        .hero-title {
          font-size: 1.7rem !important;
        }
        .hero-description {
          font-size: 0.75rem !important;
        }
        .hero-stat-number {
          font-size: 1rem !important;
        }
      }

      @media (min-width: 481px) and (max-width: 640px) {
        .hero-title {
          font-size: 2rem !important;
        }
        .hero-description {
          font-size: 0.9rem !important;
        }
      }

      /* Landscape mode optimizations */
      @media (max-height: 500px) and (orientation: landscape) {
        .hero-content {
          padding-top: 0.5rem !important;
          padding-bottom: 0.5rem !important;
        }
        .hero-title {
          font-size: 1.2rem !important;
          margin-bottom: 0.25rem !important;
        }
        .hero-description {
          font-size: 0.6rem !important;
          margin-bottom: 0.5rem !important;
        }
        .hero-buttons {
          margin-bottom: 0.5rem !important;
        }
        .hero-stats {
          gap: 0.25rem !important;
        }
      }

      /* Ensure video covers properly on all devices */
      .hero-video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      /* Optimize for different aspect ratios */
      @media (aspect-ratio: 9/16) {
        .hero-video {
          object-fit: cover;
        }
      }

      @media (aspect-ratio: 9/19.5) {
        .hero-video {
          object-fit: cover;
        }
      }
    `;
    document.head.appendChild(style);

    // Set page title for better SEO
    document.title = "Laminance Cabinetry | Custom Kitchen Cabinets & Acrylic Cabinets";
    
    // Add meta description dynamically
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = 'Custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions by Laminance Cabinetry. European-style kitchen cabinets with premium finishes and full customization.';
    document.head.appendChild(metaDescription);
    
    // Preload video for caching
    const preloadVideo = () => {
      if (videoRef.current) {
        videoRef.current.preload = 'auto';
        videoRef.current.load();
      }
    };
    
    preloadVideo();
    
    // Check if video is already in cache
    if (sessionStorage.getItem('videoCached')) {
      setShowLoaderImage(false);
    }
    
    // Cleanup
    return () => {
      document.head.removeChild(metaDescription);
      document.head.removeChild(style);
    };
  }, []);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isMusicOn) {
        audio.pause();
      } else {
        audio.play().catch((err) => console.warn("Error playing audio:", err));
      }
      setIsMusicOn((prev) => !prev);
    }
  };

  const handleVideoLoad = () => {
    setIsLoaded(true);
    setVideoError(false);
    setShowLoaderImage(false);
    sessionStorage.setItem('videoCached', 'true');
  };

  const handleVideoError = () => {
    console.error("Video failed to load");
    setVideoError(true);
    setIsLoaded(true);
  };

  const handleGetFreeDesign = () => {
    navigate('/catalog');
  };

  const handleViewPortfolio = () => {
    navigate('/gallery');
  };

  // Schema data for structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CabinetStore",
    "@id": "https://laminance.com/#business",
    "name": "Laminance Cabinetry",
    "description": "Laminance Cabinetry specializes in custom kitchen cabinets, acrylic cabinets, and modern cabinetry solutions.",
    "url": "https://laminance.com",
    "telephone": "+18624506069",
    "email": "contactus@laminance.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.1220",
      "longitude": "-74.5804"
    },
    "areaServed": {
      "@type": "State",
      "name": "New Jersey"
    },
    "knowsAbout": [
      "Custom Kitchen Cabinets",
      "Acrylic Cabinets",
      "Modern Cabinetry",
      "Cabinet Installation",
      "Kitchen Remodeling",
      "European Cabinets"
    ],
    "priceRange": "$$",
    "openingHours": "Mo-Fr 09:00-18:00",
    "image": [
      cloudinaryLoaderImage,
      "https://laminance.com/assets/Images/logo.png"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "bestRating": "5",
      "ratingCount": "200"
    }
  };

  // Breadcrumb structured data
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://laminance.com"
    }]
  };

  return (
    <section 
      className="relative h-screen w-full overflow-hidden" 
      style={{ backgroundColor: "var(--color-primary-500)" }}
      role="banner" 
      aria-label="Laminance Cabinetry - Custom Kitchen Cabinets & Acrylic Cabinets"
      itemScope
      itemType="https://schema.org/CabinetStore"
    >
      {/* Structured Data for SEO - Multiple Schemas */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>

      {/* Additional Local Business Schema for better local SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Laminance Cabinetry",
          "image": cloudinaryLoaderImage,
          "@id": "https://laminance.com",
          "url": "https://laminance.com",
          "telephone": "+18624506069",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "418 Rt 23",
            "addressLocality": "Franklin",
            "addressRegion": "NJ",
            "postalCode": "07416",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "41.1220",
            "longitude": "-74.5804"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "18:00"
          }
        })}
      </script>

      {/* Audio Player */}
      <audio 
        ref={audioRef} 
        loop 
        preload="auto" 
        aria-hidden="true"
        className="hidden"
      >
        <source src="/assets/Images/song.mp3" type="audio/mp3" />
        Your browser does not support the audio element.
      </audio>

      {/* Loader Image - Shows full screen until video loads */}
      {showLoaderImage && (
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('${cloudinaryLoaderImage}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
          aria-hidden="true"
          role="img"
          aria-label="Loading Laminance Cabinetry video"
        />
      )}

      {/* Video Background - Shows after loading */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="relative w-full h-full min-h-screen">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            className="hero-video"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onLoadedData={handleVideoLoad}
            onCanPlay={handleVideoLoad}
            onError={handleVideoError}
            title="Laminance Cabinetry Custom Kitchen Cabinet Installation Video"
            poster={cloudinaryLoaderImage}
            aria-label="Video showcasing Laminance Cabinetry's custom kitchen cabinet installations and acrylic cabinet designs"
          >
            <source 
              src={cloudinaryVideoUrl} 
              type="video/mp4" 
            />
            Your browser does not support the video tag.
          </video>
        </div>
        
        {/* Gradient Overlay - Always visible over video OR loader image */}
        <div 
          className="absolute inset-0" 
          style={{ 
            background: "linear-gradient(to top, var(--overlay-dark) 0%, var(--overlay-semi-dark) 50%, transparent 100%)" 
          }}
        ></div>
        
        {/* Background Decorative Elements */}
        <div 
          className="absolute top-0 left-0 w-72 h-72 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-20 blur-3xl" 
          aria-hidden="true"
          style={{
            backgroundColor: "var(--color-primary-600)",
          }}
        ></div>
        <div 
          className="absolute bottom-0 right-0 w-96 h-96 rounded-full translate-x-1/3 translate-y-1/3 opacity-20 blur-3xl" 
          aria-hidden="true"
          style={{
            backgroundColor: "var(--color-primary-600)",
          }}
        ></div>
      </div>

      {/* Main Content - with working transitions */}
      <div className="relative z-10 flex h-full items-center justify-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div 
            className={`w-full flex flex-col items-center sm:items-start transition-all duration-700 ease-out ${
              isLoaded ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-100'
            }`}
            itemScope
            itemType="https://schema.org/Service"
          >
            {/* Main content wrapper with max-width */}
            <div className="w-full max-w-[280px] xs:max-w-[320px] sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl text-center sm:text-left">
              
              {/* Location Badge - with staggered animation */}
              <div 
                className={`inter-secondary mb-2 xs:mb-3 sm:mb-4 inline-flex items-center gap-1 xs:gap-1.5 sm:gap-2 text-[10px] xs:text-xs sm:text-sm md:text-base font-medium rounded-full px-2 xs:px-3 sm:px-4 py-1 xs:py-1.5 sm:py-2 backdrop-blur-sm mx-auto sm:mx-0 transition-all duration-500 ${
                  isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-100'
                }`}
                style={{ 
                  backgroundColor: "var(--color-primary-500)",
                  color: "var(--color-white)",
                  transitionDelay: isLoaded ? '100ms' : '0ms'
                }}
                itemProp="areaServed"
                itemScope
                itemType="https://schema.org/Place"
              >
                <svg className="w-2 h-2 xs:w-2.5 xs:h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span itemProp="name" className="whitespace-nowrap">Serving New Jersey</span>
              </div>

              {/* Main Headline - with staggered animation */}
              <h1 
                className={`bell-mt-primary hero-title text-2xl sm:text-4xl md:text-5xl lg:text-5xl font-bold mb-2 sm:mb-4 leading-tight transition-all duration-500 ${
                  isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-100'
                }`} 
                style={{ 
                  color: "var(--color-white)",
                  transitionDelay: isLoaded ? '200ms' : '0ms'
                }}
              >
                Custom Kitchen Cabinets & Premium Cabinet Solutions by{' '}
                <span style={{ color: "var(--color-accent-500)" }}>Laminance Cabinetry</span>
              </h1>
              
              {/* Description - with staggered animation */}
              <p 
                className={`inter-secondary hero-description text-sm sm:text-lg md:text-xl mb-4 sm:mb-8 max-w-2xl transition-all duration-500 ${
                  isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-100'
                }`} 
                style={{ 
                  color: "rgba(255, 255, 255, 0.9)",
                  transitionDelay: isLoaded ? '300ms' : '0ms'
                }}
              >
                Complete kitchen transformations with all-inclusive packages. From concept to completion, we handle everything.
              </p>

              {/* CTA Buttons - with staggered animation */}
              <div 
                className={`inter-secondary hero-buttons mb-4 xs:mb-5 sm:mb-8 md:mb-10 lg:mb-12 flex flex-col xs:flex-row gap-2 xs:gap-3 sm:gap-4 justify-center sm:justify-start transition-all duration-500 ${
                  isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-100'
                }`}
                style={{ transitionDelay: isLoaded ? '400ms' : '0ms' }}
              >
                {/* Primary CTA Button */}
                <button 
                  onClick={handleGetFreeDesign}
                  className="group relative overflow-hidden rounded-full px-4 xs:px-5 sm:px-5 md:px-6 lg:px-7 xl:px-8 py-2 xs:py-2.5 sm:py-3 md:py-3.5 lg:py-4 font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-xs xs:text-sm sm:text-sm md:text-base lg:text-lg"
                  style={{ 
                    background: "var(--gradient-cta)",
                    color: "var(--color-white)",
                  }}
                  aria-label="Get Free Kitchen Cabinet Design Consultation - Redirects to Catalog Page"
                  itemProp="potentialAction"
                  itemScope
                  itemType="https://schema.org/Action"
                >
                  <meta itemProp="name" content="View Brochure" />
                  <span className="relative z-10 flex items-center gap-1 xs:gap-1.5 sm:gap-2 justify-center whitespace-nowrap">
                    <span className="hidden xs:inline">View Brochure</span>
                    <span className="xs:hidden">Free Design</span>
                    <svg className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-4 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </button>
                
                {/* Secondary CTA Button */}
                <button 
                  onClick={handleViewPortfolio}
                  className="inter-secondary group rounded-full px-4 xs:px-5 sm:px-5 md:px-6 lg:px-7 xl:px-8 py-2 xs:py-2.5 sm:py-3 md:py-3.5 lg:py-4 font-semibold backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-xs xs:text-sm sm:text-sm md:text-base lg:text-lg"
                  style={{ 
                    color: "var(--color-white)",
                    backgroundColor: "var(--bg-white-10)",
                    border: "2px solid var(--border-white-10)",
                  }}
                  aria-label="View Our Kitchen Cabinet Portfolio Gallery - Redirects to Gallery Page"
                  itemProp="potentialAction"
                  itemScope
                  itemType="https://schema.org/Action"
                >
                  <meta itemProp="name" content="View Kitchen Portfolio" />
                  <span className="relative z-10 flex items-center gap-1 xs:gap-1.5 sm:gap-2 justify-center whitespace-nowrap">
                    <span className="hidden xs:inline">View Kitchen Portfolio</span>
                    <span className="xs:hidden">Portfolio</span>
                    <svg className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                </button>
              </div>

              {/* Trust Indicators - Stats Section with staggered animations */}
              <div 
                className="inter-secondary hero-stats flex flex-wrap justify-center sm:justify-start gap-2 sm:gap-3 md:gap-4 lg:gap-5 xl:gap-6"
                itemScope
                itemType="https://schema.org/AggregateRating"
              >
                <meta itemProp="ratingValue" content="5" />
                <meta itemProp="bestRating" content="5" />
                <meta itemProp="ratingCount" content="200" />
                <meta itemProp="itemReviewed" content="Laminance Cabinetry Custom Kitchen Cabinets" />
                
                {[
                  { 
                    number: '200+', 
                    text: 'Tile Selection Option', 
                    shortText: 'Tiles',
                    icon: '🏠',
                    schema: 'numberOfKitchens',
                    ariaLabel: '200 plus custom kitchen cabinet projects completed'
                  },
                  { 
                    number: '100+', 
                    text: 'Cabinet Color Options', 
                    shortText: 'Colors',
                    icon: '🎨',
                    schema: 'colorOptions',
                    ariaLabel: '300 plus cabinet finish and color options available'
                  },
                  { 
                    number: '5★', 
                    text: 'Customer Rating', 
                    shortText: 'Rating',
                    icon: '⭐',
                    schema: 'ratingValue',
                    ariaLabel: 'Five star customer rating for cabinet installation'
                  }
                ].map((item, index) => (
                  <div 
                    key={index}
                    className={`hero-stat-item transition-all duration-500 ${
                      isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-100'
                    }`}
                    style={{ 
                      transitionDelay: isLoaded ? `${500 + index * 100}ms` : '0ms',
                      flex: '1 1 auto',
                      minWidth: '85px',
                      maxWidth: '140px'
                    }}
                    itemProp={item.schema}
                    content={item.number.replace('+', '').replace('★', '')}
                    aria-label={item.ariaLabel}
                  >
                    <div 
                      className="text-center backdrop-blur-sm rounded-lg xs:rounded-xl sm:rounded-2xl p-2 xs:p-2.5 sm:p-3 md:p-3.5 lg:p-4 border transition-all duration-300 hover:scale-105"
                      style={{ 
                        backgroundColor: "var(--bg-white-10)",
                        borderColor: "var(--border-white-10)",
                      }}
                    >
                      <div 
                        className="text-sm xs:text-base sm:text-xl md:text-2xl lg:text-3xl mb-1 xs:mb-1.5 sm:mb-2" 
                        role="img" 
                        aria-hidden="true"
                      >
                        {item.icon}
                      </div>
                      <div 
                        className="hero-stat-number text-xs xs:text-sm sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-bold"
                        style={{ color: "var(--color-white)" }}
                      >
                        {item.number}
                      </div>
                      <div 
                        className="hero-stat-text text-[8px] xs:text-xs sm:text-sm md:text-base mt-1 leading-tight font-medium"
                        style={{ color: "var(--color-gray-300)" }}
                      >
                        <span className="hidden xs:inline">{item.text}</span>
                        <span className="xs:hidden">{item.shortText}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Music Toggle Button */}
      <button
        onClick={toggleMusic}
        className="inter-secondary absolute top-3 xs:top-4 sm:top-5 md:top-6 right-3 xs:right-4 sm:right-5 md:right-6 z-20 flex h-8 w-8 xs:h-9 xs:w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 lg:h-12 lg:w-12 items-center justify-center rounded-full backdrop-blur-sm border transition-all duration-300 hover:scale-110 active:scale-95 focus:outline-none"
        style={{ 
          backgroundColor: "var(--bg-primary-500-20)",
          borderColor: "var(--border-white-10)",
        }}
        aria-label={isMusicOn ? "Turn off background music" : "Turn on background music for better browsing experience"}
        aria-pressed={isMusicOn}
        role="switch"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          className="h-4 w-4 xs:h-4.5 xs:w-4.5 sm:h-5 sm:w-5 md:h-5.5 md:w-5.5 lg:h-6 lg:w-6"
          style={{ fill: "var(--color-white)" }}
          aria-hidden="true"
        >
          {isMusicOn ? (
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          ) : (
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
          )}
        </svg>
      </button>

      {/* Decorative Elements */}
      <div 
        className="absolute top-1/4 right-3 xs:right-4 sm:right-6 md:right-8 lg:right-10 w-1 h-1 xs:w-1.5 xs:h-1.5 sm:w-2 sm:h-2 rounded-full animate-pulse" 
        style={{ backgroundColor: "var(--color-white)" }}
        aria-hidden="true"
      ></div>
      <div 
        className="absolute bottom-1/3 left-3 xs:left-4 sm:left-5 md:left-6 w-0.5 h-0.5 xs:w-1 xs:h-1 sm:w-1.5 sm:h-1.5 rounded-full animate-pulse delay-1000" 
        style={{ backgroundColor: "var(--color-gray-300)" }}
        aria-hidden="true"
      ></div>
      <div 
        className="absolute top-1/2 right-6 xs:right-8 sm:right-10 md:right-14 lg:right-20 w-0.5 h-0.5 xs:w-1 xs:h-1 sm:w-1.5 sm:h-1.5 rounded-full animate-pulse delay-500" 
        style={{ backgroundColor: "var(--color-gray-400)" }}
        aria-hidden="true"
      ></div>
      
      {/* Bottom Gradient */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-16 xs:h-20 sm:h-24 md:h-28 lg:h-32 pointer-events-none"
        style={{
          background: "var(--gradient-footer-bottom)"
        }}
        aria-hidden="true"
      ></div>

      {/* Scroll Indicator for larger screens */}
      <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center">
          <span 
            className="inter-secondary text-xs mb-2"
            style={{ color: "var(--color-gray-400)" }}
          >
            Scroll to explore
          </span>
          <svg 
            className="w-5 h-5" 
            style={{ color: "var(--color-gray-500)" }}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default VideoHero;